import React, { useState, useEffect } from 'react';
import {
  FaSearch,
  FaBell,
  FaEnvelope,
  FaSun,
  FaMoon,
  FaBars,
  FaTimes,
  FaCog,
  FaUser,
  FaSignOutAlt,
  FaCompress,
  FaExpand,
  FaRedoAlt
} from 'react-icons/fa';
import { FiChevronDown, FiMenu, FiX } from 'react-icons/fi';
import { MdRefresh } from 'react-icons/md';

const Navbar = ({ toggleSidebar, toggleDarkMode, darkMode, collapsed, sidebarOpen, onNavigate }) => {
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  useEffect(() => {
    const checkDevice = () => {
      setIsMobile(window.innerWidth < 768);
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    checkDevice();
    window.addEventListener('resize', checkDevice);
    return () => window.removeEventListener('resize', checkDevice);
  }, []);

  const notifications = [
    { id: 1, text: 'New order received', time: '5 min ago', unread: true, type: 'order' },
    { id: 2, text: 'Server backup completed', time: '1 hour ago', unread: true, type: 'system' },
    { id: 3, text: 'New user registered', time: '2 hours ago', unread: false, type: 'user' },
    { id: 4, text: 'Payment received', time: '3 hours ago', unread: false, type: 'payment' },
  ];

  const unreadCount = notifications.filter(n => n.unread).length;

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      console.log('Searching for:', searchQuery);
      // Implement search functionality
    }
  };

  const formatTime = () => {
    return currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = () => {
    return currentTime.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'order': return '🛒';
      case 'system': return '⚙️';
      case 'user': return '👤';
      case 'payment': return '💰';
      default: return '📌';
    }
  };

  return (
    <header className={`
      ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'}
      border-b ${darkMode ? 'border-gray-700' : 'border-red-100'}
      px-3 sm:px-4 lg:px-6 py-2 sm:py-3
      flex items-center justify-between
      transition-all duration-300
      sticky top-0 z-30
      shadow-sm
    `}>
      {/* Left Section */}
      <div className="flex items-center space-x-2 sm:space-x-4">
        {/* Menu Toggle Button */}
        <button
          onClick={toggleSidebar}
          className={`
            p-2 rounded-lg transition-all duration-200
            ${darkMode 
              ? 'hover:bg-gray-700 hover:text-red-400' 
              : 'hover:bg-red-50 hover:text-red-600'
            }
          `}
          aria-label="Toggle menu"
        >
          {sidebarOpen ? (
            <FiX className="text-xl sm:text-2xl" />
          ) : (
            <FiMenu className="text-xl sm:text-2xl" />
          )}
        </button>

        {/* Breadcrumb/Page Title - Hidden on mobile */}
        <div className="hidden sm:block">
          <h1 className="text-base sm:text-lg lg:text-xl font-semibold">
            <span className="text-red-500 mr-2">●</span>
            Dashboard
          </h1>
          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {formatDate()} • {formatTime()}
          </p>
        </div>

        {/* Search Bar - Desktop */}
        <form onSubmit={handleSearch} className="hidden md:block relative ml-4">
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className={`
              pl-10 pr-4 py-2 rounded-lg text-sm w-64 lg:w-80
              transition-all duration-200
              focus:outline-none focus:ring-2 focus:ring-red-400
              ${darkMode 
                ? 'bg-gray-700 text-white placeholder-gray-400 border-gray-600' 
                : 'bg-red-50 text-gray-800 placeholder-gray-500 border-red-100'
              }
              border
            `}
          />
          <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-sm ${darkMode ? 'text-gray-400' : 'text-red-400'}`} />
        </form>

        {/* Mobile Search Toggle */}
        <button
          onClick={() => setSearchOpen(!searchOpen)}
          className={`
            md:hidden p-2 rounded-lg transition-all duration-200
            ${darkMode 
              ? 'hover:bg-gray-700 hover:text-red-400' 
              : 'hover:bg-red-50 hover:text-red-600'
            }
          `}
          aria-label="Search"
        >
          <FaSearch className="text-lg" />
        </button>
      </div>

      {/* Mobile Search Bar - Expandable */}
      {searchOpen && (
        <div className={`
          absolute left-0 right-0 top-full p-3 shadow-lg
          ${darkMode ? 'bg-gray-800' : 'bg-white'}
          border-t ${darkMode ? 'border-gray-700' : 'border-red-100'}
          md:hidden z-40
        `}>
          <form onSubmit={handleSearch} className="relative">
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`
                w-full pl-10 pr-4 py-2.5 rounded-lg text-sm
                focus:outline-none focus:ring-2 focus:ring-red-400
                ${darkMode 
                  ? 'bg-gray-700 text-white placeholder-gray-400' 
                  : 'bg-red-50 text-gray-800 placeholder-gray-500'
                }
              `}
              autoFocus
            />
            <FaSearch className={`absolute left-3 top-1/2 transform -translate-y-1/2 text-sm ${darkMode ? 'text-gray-400' : 'text-red-400'}`} />
          </form>
        </div>
      )}

      {/* Right Section */}
      <div className="flex items-center space-x-1 sm:space-x-2 md:space-x-3">
        {/* Dark Mode Toggle */}
        {/* <button
          onClick={toggleDarkMode}
          className={`
            p-2 rounded-lg transition-all duration-200
            ${darkMode 
              ? 'hover:bg-gray-700 text-yellow-400' 
              : 'hover:bg-red-50 text-gray-600'
            }
          `}
          title={darkMode ? 'Light Mode' : 'Dark Mode'}
          aria-label="Toggle dark mode"
        >
          {darkMode ? <FaSun className="text-lg" /> : <FaMoon className="text-lg" />}
        </button> */}

        {/* Fullscreen Button - Hidden on mobile */}
        <button
          onClick={toggleFullscreen}
          title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          className={`
            hidden sm:block p-2 rounded-lg transition-all duration-200
            ${darkMode 
              ? 'hover:bg-gray-700' 
              : 'hover:bg-red-50'
            }
          `}
        >
          {isFullscreen ? <FaCompress className="text-lg" /> : <FaExpand className="text-lg" />}
        </button>

        {/* Notifications */}
        <div className="relative">
          <button
            onClick={() => setNotificationOpen(!notificationOpen)}
            className={`
              relative p-2 rounded-lg transition-all duration-200
              ${darkMode 
                ? 'hover:bg-gray-700' 
                : 'hover:bg-red-50'
              }
            `}
            aria-label="Notifications"
          >
            <FaBell className="text-lg" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 min-w-[20px] h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center px-1">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>

          {/* Notification Dropdown */}
          {notificationOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setNotificationOpen(false)}
              ></div>
              <div className={`
                absolute right-0 mt-2 w-80 sm:w-96
                ${darkMode ? 'bg-gray-800' : 'bg-white'}
                rounded-lg shadow-xl border ${darkMode ? 'border-gray-700' : 'border-red-100'}
                z-50
                max-h-[calc(100vh-100px)]
                overflow-hidden
                flex flex-col
              `}>
                <div className={`p-3 sm:p-4 border-b ${darkMode ? 'border-gray-700' : 'border-red-100'} flex justify-between items-center`}>
                  <div>
                    <h3 className="font-semibold">Notifications</h3>
                    <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      You have {unreadCount} unread messages
                    </p>
                  </div>
                  <button className="text-red-500 hover:text-red-600 text-sm flex items-center">
                    <MdRefresh className="mr-1" /> Refresh
                  </button>
                </div>
                
                <div className="overflow-y-auto flex-1">
                  {notifications.map(notification => (
                    <div
                      key={notification.id}
                      className={`
                        p-3 sm:p-4 border-b ${darkMode ? 'border-gray-700 hover:bg-gray-700' : 'border-red-50 hover:bg-red-50'}
                        transition-all duration-200 cursor-pointer
                        ${notification.unread ? (darkMode ? 'bg-gray-700/50' : 'bg-red-50/50') : ''}
                      `}
                    >
                      <div className="flex items-start space-x-3">
                        <div className="text-xl">{getNotificationIcon(notification.type)}</div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm sm:text-base truncate">{notification.text}</p>
                          <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'} mt-1`}>
                            {notification.time}
                          </p>
                        </div>
                        {notification.unread && (
                          <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className={`p-2 text-center border-t ${darkMode ? 'border-gray-700' : 'border-red-100'}`}>
                  <button
                    className={`block w-full py-2 text-sm ${darkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-800'}`}
                  >
                    View all notifications
                  </button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* User Profile */}
        <div className="relative">
          <button
            onClick={() => setProfileOpen(!profileOpen)}
            className={`
              flex items-center space-x-2 p-1 pr-2 rounded-lg transition-all duration-200
              ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-red-50'}
              ${profileOpen ? (darkMode ? 'bg-gray-700' : 'bg-red-50') : ''}
            `}
          >
            <img
              src="https://ui-avatars.com/api/?name=Admin+User&background=dc2626&color=fff&bold=true"
              alt="Admin"
              className="w-7 h-7 sm:w-8 sm:h-8 rounded-full border-2 border-red-500"
            />
            <div className="hidden md:block text-left">
              <p className="text-sm font-medium">Admin User</p>
              <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Administrator</p>
            </div>
            <FiChevronDown className="hidden md:block text-sm" />
          </button>

          {/* Profile Dropdown */}
          {profileOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setProfileOpen(false)}
              ></div>
              <div className={`
                absolute right-0 mt-2 w-56 sm:w-64
                ${darkMode ? 'bg-gray-800' : 'bg-white'}
                rounded-lg shadow-xl border ${darkMode ? 'border-gray-700' : 'border-red-100'}
                z-50
                overflow-hidden
              `}>
                <div className={`p-4 border-b ${darkMode ? 'border-gray-700' : 'border-red-100'}`}>
                  <div className="flex items-center space-x-3">
                    <img
                      src="https://ui-avatars.com/api/?name=Admin+User&background=dc2626&color=fff&bold=true"
                      alt="Admin"
                      className="w-12 h-12 rounded-full border-2 border-red-500"
                    />
                    <div className="min-w-0">
                      <p className="font-semibold truncate">Admin User</p>
                      <p className={`text-sm truncate ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        admin@example.com
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="py-1">
                  <button
                    onClick={() => {
                      setProfileOpen(false);
                      onNavigate('/profile');
                    }}
                    className={`w-full text-left px-4 py-3 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-red-50'} transition-colors duration-200 flex items-center`}
                  >
                    <FaUser className="mr-3 text-red-500" /> Profile
                  </button>
                  <button
                    onClick={() => {
                      setProfileOpen(false);
                      onNavigate('/settings');
                    }}
                    className={`w-full text-left px-4 py-3 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-red-50'} transition-colors duration-200 flex items-center`}
                  >
                    <FaCog className="mr-3 text-red-500" /> Settings
                  </button>
                </div>
                
                <div className={`border-t ${darkMode ? 'border-gray-700' : 'border-red-100'}`}></div>
                
                <button
                  onClick={() => {
                    // Handle logout
                    sessionStorage.clear();
                    window.location.href = '/';
                  }}
                  className={`w-full text-left px-4 py-3 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-red-50'} text-red-600 dark:text-red-400 transition-colors duration-200 flex items-center`}
                >
                  <FaSignOutAlt className="mr-3" /> Logout
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Navbar;